import React, { useState } from 'react';
import type {
  AttentionItem,
  HoldInfo,
  JiraFields,
  JiraUser,
  CrossReference,
} from '../../domain/types';
import { useZunelo } from '../../context/ZuneloContext';
import { Badge, Pill, Avatar, BrandIcon } from '../../ui/common';
import { ProviderItemDetail } from '../detail/ProviderItemDetail';
import { BasicMarkdown } from '../../ui/common/BasicMarkdown';
import { FlowService } from '../../services/flowService';
import { formatRelativeTime } from '../../domain/types';
import { JiraCriteriaSection } from './JiraCriteriaSection';
import { JiraSubtasksSection } from './JiraSubtasksSection';
import { buildJiraBrowseUrl } from './jiraBrowseUrl';
import { JIRA_STANDARD_STATUSES } from './jiraCardVisuals';
import { linkedPrChipLabelFromAttentionItem } from '../../domain/projectCardIdentity';
import { ProviderCommentComposer } from '../../ui/common/ProviderCommentComposer';
import { openSettingsSection } from '../settings/settingsDeepLink';

export interface JiraDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AttentionItem;
}

interface JiraModalHeaderProps {
  item: AttentionItem;
  issueType: string;
  priority: string;
  workflow: string;
  availableTransitions: string[];
  nextWorkflow?: string | null;
  jiraBrowseUrl: string;
  copiedKey: boolean;
  onCopyKey: (e: React.MouseEvent) => void;
  onUpdateWorkflow: (ticket: string, next: string) => void;
  onClose: () => void;
}

function JiraModalHeader({
  item,
  issueType,
  priority,
  workflow,
  availableTransitions,
  nextWorkflow,
  jiraBrowseUrl,
  copiedKey,
  onCopyKey,
  onUpdateWorkflow,
  onClose,
}: JiraModalHeaderProps) {
  const priorityTone =
    priority === 'P0' || priority === 'Muy Alta'
      ? 'bad'
      : priority === 'P1' || priority === 'Alta'
        ? 'warn'
        : 'flat';

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 20px',
        borderBottom: '1px solid var(--z-fill-2)',
        background: 'var(--z-surface-subtle)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <BrandIcon source="jira" size={18} />
        <Badge label={issueType} tone="accent" />
        <button
          type="button"
          data-testid="jira-modal-copy-key"
          onClick={onCopyKey}
          title="Copiar clave"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            borderRadius: 6,
            border: 'none',
            background: 'var(--z-fill-2)',
            color: 'var(--z-text)',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <span>{item.ticket || item.primary}</span>
          <span style={{ fontSize: 10, color: 'var(--z-dim)' }}>{copiedKey ? '✓' : '📋'}</span>
        </button>
        <Pill label={priority} tone={priorityTone} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--z-dim)' }}>Estado:</label>
          <select
            data-testid="jira-modal-workflow-select"
            value={workflow}
            onChange={(e) => item.ticket && onUpdateWorkflow(item.ticket, e.target.value)}
            style={{
              height: 32,
              padding: '0 10px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--z-fill-2)',
              fontFamily: 'inherit',
              fontSize: 12.5,
              fontWeight: 600,
              color: 'var(--z-text)',
              cursor: 'pointer',
            }}
          >
            {availableTransitions.map((step) => (
              <option key={step} value={step}>
                {step}
              </option>
            ))}
          </select>
          {nextWorkflow ? (
            <button
              type="button"
              data-testid="jira-modal-next-workflow"
              onClick={() => item.ticket && onUpdateWorkflow(item.ticket, nextWorkflow)}
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--z-accent-soft)',
                color: 'var(--z-accent-ink)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              → {nextWorkflow}
            </button>
          ) : null}
        </div>

        {jiraBrowseUrl ? (
          <a
            href={jiraBrowseUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jira-modal-open-external"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              background: 'var(--z-fill)',
              color: 'var(--z-text-2)',
              fontSize: 12.5,
              fontWeight: 600,
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            <span>Abrir en Jira</span>
            <span style={{ fontSize: 11 }}>↗</span>
          </a>
        ) : null}

        <button
          type="button"
          data-testid="jira-modal-close"
          aria-label="Cerrar modal"
          onClick={onClose}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 8,
            border: 'none',
            background: 'var(--z-fill-2)',
            color: 'var(--z-text-2)',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          ✕
        </button>
      </div>
    </header>
  );
}

function JiraModalAiBanner({
  aiText,
  summaryBusy,
  onGenerateSummary,
}: {
  aiText: string;
  summaryBusy: boolean;
  onGenerateSummary: () => void;
}) {
  return (
    <div
      data-testid="jira-modal-ai-summary"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '14px 16px',
        borderRadius: 12,
        background: 'var(--z-accent-wash)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--z-accent-ink)',
          }}
        >
          Resumen Inteligente
        </span>
        <button
          type="button"
          data-testid="jira-modal-summary-refresh"
          disabled={summaryBusy}
          onClick={onGenerateSummary}
          style={{
            marginLeft: 'auto',
            height: 24,
            padding: '0 10px',
            borderRadius: 7,
            border: 'none',
            cursor: summaryBusy ? 'wait' : 'pointer',
            fontSize: 11,
            fontWeight: 600,
            background: 'var(--z-accent-soft)',
            color: 'var(--z-accent-ink)',
            opacity: summaryBusy ? 0.7 : 1,
          }}
        >
          {summaryBusy ? 'Generando…' : 'Regenerar'}
        </button>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 13.5,
          lineHeight: 1.6,
          color: 'var(--z-accent-copy)',
          textWrap: 'pretty',
        }}
      >
        {aiText}
      </p>
    </div>
  );
}

function JiraModalDescription({ description }: { description: string }) {
  return (
    <div data-testid="jira-modal-description-section">
      <h2
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--z-dim)',
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Descripción
      </h2>
      {description.trim() ? (
        <div
          style={{
            padding: '16px 18px',
            borderRadius: 12,
            background: 'var(--z-plate)',
            lineHeight: 1.6,
          }}
        >
          <BasicMarkdown text={description} />
        </div>
      ) : (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: 'var(--z-plate)',
            color: 'var(--z-dim)',
            fontSize: 13,
            fontStyle: 'italic',
          }}
        >
          Sin descripción detallada disponible en este ticket.
        </div>
      )}
    </div>
  );
}

function JiraModalLinkedPrs({
  linkedPullRequests,
  onNavigateToGitHub,
}: {
  linkedPullRequests: AttentionItem[];
  onNavigateToGitHub: (id: string) => void;
}) {
  if (linkedPullRequests.length === 0) return null;

  return (
    <div
      data-testid="jira-modal-linked-prs"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <h2
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--z-dim)',
          margin: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Pull Requests vinculados en GitHub
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {linkedPullRequests.map((pr) => (
          <div
            key={pr.id}
            onClick={() => onNavigateToGitHub(pr.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'var(--z-plate)',
              cursor: 'pointer',
            }}
          >
            <BrandIcon source="github" size={16} />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--z-ok-ink, #065f46)',
              }}
            >
              {linkedPrChipLabelFromAttentionItem(pr)}
            </span>
            <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, color: 'var(--z-text-2)' }}>
              {pr.title}
            </span>
            <span style={{ fontSize: 11, color: 'var(--z-dim)' }}>{pr.age}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function JiraModalRefs({
  refs,
  onNavigateToJira,
  isJiraRefResolved,
}: {
  refs: CrossReference[];
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
}) {
  if (!refs || refs.length === 0) return null;

  return (
    <div data-testid="jira-modal-refs" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h2
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--z-dim)',
          margin: 0,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Tickets vinculados
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {refs.map((rf) => {
          const resolved = isJiraRefResolved ? isJiraRefResolved(rf.key) : true;
          return (
            <button
              type="button"
              key={rf.key}
              aria-label={resolved ? `Ver ${rf.key}` : `${rf.key}: no está cargado en Zunelo`}
              aria-disabled={!resolved}
              disabled={!resolved}
              title={resolved ? undefined : 'No está cargado en Zunelo'}
              onClick={() => {
                if (resolved) onNavigateToJira(rf.key);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 999,
                cursor: resolved ? 'pointer' : 'not-allowed',
                opacity: resolved ? 1 : 0.55,
                background: 'var(--z-accent-wash)',
                border: 'none',
              }}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: 'var(--z-accent-ink)',
                }}
              >
                {rf.key}
              </span>
              <span style={{ fontSize: 12, color: 'var(--z-accent-copy)' }}>{rf.why}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function JiraModalSidebar({
  holdInfo,
  assignee,
  reporter,
  sprint,
  storyPoints,
  components,
  labels,
  createdAt,
  updatedAt,
  externalSignals = [],
}: {
  holdInfo?: HoldInfo;
  assignee?: JiraUser;
  reporter?: JiraUser;
  sprint?: string;
  storyPoints?: number;
  components: string[];
  labels: string[];
  createdAt?: string;
  updatedAt?: string;
  externalSignals?: Array<{
    src: string;
    label: string;
    tone: 'ok' | 'warn' | 'bad' | 'accent' | 'flat';
  }>;
}) {
  return (
    <aside
      style={{
        flex: '0 0 280px',
        minWidth: 260,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {holdInfo ? (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: 'var(--z-plate)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Pill label={holdInfo.chip} tone={holdInfo.kind === 'release' ? 'warn' : 'bad'} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--z-text-2)' }}>
              {holdInfo.title}
            </span>
          </div>
          <p
            style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.5, color: 'var(--z-muted-3)' }}
          >
            {holdInfo.note}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {holdInfo.steps.map((st, sidx) => (
              <Badge key={sidx} label={st.label} tone={st.tone} />
            ))}
          </div>
        </div>
      ) : null}

      <div
        style={{
          padding: '16px',
          borderRadius: 14,
          background: 'var(--z-plate)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--z-dim)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Detalles del ticket
        </h3>

        <div>
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: 'var(--z-dim)',
              display: 'block',
              marginBottom: 5,
            }}
          >
            Asignado a
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar name={assignee?.displayName || 'Sin asignar'} size="sm" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--z-text)' }}>
                {assignee?.displayName || 'Sin asignar'}
              </div>
              {assignee?.emailAddress ? (
                <div style={{ fontSize: 11, color: 'var(--z-dim)' }}>{assignee.emailAddress}</div>
              ) : null}
            </div>
          </div>
        </div>

        {reporter ? (
          <div>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--z-dim)',
                display: 'block',
                marginBottom: 5,
              }}
            >
              Informador
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar name={reporter.displayName} size="sm" />
              <span style={{ fontSize: 12.5, color: 'var(--z-text-2)' }}>
                {reporter.displayName}
              </span>
            </div>
          </div>
        ) : null}

        {sprint ? (
          <div>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--z-dim)',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Sprint
            </span>
            <Badge label={sprint} tone="flat" />
          </div>
        ) : null}

        {storyPoints !== undefined ? (
          <div>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--z-dim)',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Puntos de historia
            </span>
            <span
              style={{
                display: 'inline-block',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 6,
                background: 'var(--z-fill-2)',
                color: 'var(--z-text)',
              }}
            >
              {storyPoints} pts
            </span>
          </div>
        ) : null}

        {components.length > 0 ? (
          <div>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--z-dim)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Componentes
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {components.map((comp) => (
                <span
                  key={comp}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: 'var(--z-fill-2)',
                    color: 'var(--z-text-2)',
                  }}
                >
                  {comp}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {labels.length > 0 ? (
          <div>
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: 'var(--z-dim)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Etiquetas
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {labels.map((lbl) => (
                <Badge key={lbl} label={lbl} tone="flat" />
              ))}
            </div>
          </div>
        ) : null}

        {createdAt || updatedAt ? (
          <div style={{ borderTop: '1px solid var(--z-fill-2)', paddingTop: 10 }}>
            {createdAt ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  color: 'var(--z-dim)',
                  marginBottom: 4,
                }}
              >
                <span>Creado:</span>
                <span>{formatRelativeTime(createdAt)}</span>
              </div>
            ) : null}
            {updatedAt ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  color: 'var(--z-dim)',
                }}
              >
                <span>Actualizado:</span>
                <span>{formatRelativeTime(updatedAt)}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {externalSignals.length > 0 ? (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 14,
            background: 'var(--z-plate)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--z-dim)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Telemetría & Señales
          </h3>
          {externalSignals.map((sig, sidx) => (
            <div
              key={sidx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                padding: '6px 8px',
                borderRadius: 8,
                background: 'var(--z-fill)',
              }}
            >
              <Badge label={sig.src} tone={sig.tone} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sig.label}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

export const JiraDetailModal: React.FC<JiraDetailModalProps> = ({
  isOpen,
  onClose,
  item: itemProp,
}) => {
  const {
    getItemById,
    updateWorkflowState,
    toggleAcceptanceCriterion,
    addAcceptanceCriterion,
    updateAcceptanceCriterion,
    deleteAcceptanceCriterion,
    cycleSubtaskState,
    generateIssueSummary,
    addJiraComment,
    generateJiraCommentDraft,
    getGitHubItemsByTicket,
    getJiraItemByKey,
    navigateToItem,
    getHoldInfo,
    getCrossReferences,
  } = useZunelo();

  const item = getItemById(itemProp.id) || itemProp;
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  if (!isOpen) return null;

  const rawFields: JiraFields | undefined = item.rawJiraIssue?.fields;
  const workflow = item.workflow || rawFields?.status || 'Tareas por hacer';
  const holdInfo = getHoldInfo(item.ticket);
  const linkedPullRequests = getGitHubItemsByTicket(item.ticket);
  const crossRefs = getCrossReferences(item);
  const availableTransitions = Array.from(
    new Set([...JIRA_STANDARD_STATUSES, ...FlowService.getAvailableTransitions(workflow)]),
  );
  const nextWorkflow = FlowService.getNextWorkflowState(workflow);

  const jiraBrowseUrl = buildJiraBrowseUrl(item.ticket, {
    rawSelfUrl: item.rawJiraIssue?.self,
  });

  const handleCopyKey = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.ticket && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(item.ticket)
        .then(() => {
          setCopiedKey(true);
          setTimeout(() => setCopiedKey(false), 2000);
        })
        .catch(() => {
          // Ignore clipboard permission errors in restricted environments
        });
    }
  };

  const handleGenerateSummary = () => {
    if (!item.ticket || summaryBusy) return;
    setSummaryBusy(true);
    void generateIssueSummary(item.ticket).finally(() => setSummaryBusy(false));
  };

  const handleNavigateToJira = (key: string) => {
    const linked = getJiraItemByKey(key);
    if (linked) {
      onClose();
      navigateToItem('jira', linked.id);
    }
  };

  const handleNavigateToGitHub = (id: string) => {
    onClose();
    navigateToItem('github', id);
  };

  const priority = rawFields?.priority || 'Media';
  const issueType = item.type || rawFields?.issueType || 'Story';
  const description = item.desc || rawFields?.description || '';

  return (
    <ProviderItemDetail
      isOpen={isOpen}
      onClose={onClose}
      testId="jira-detail-modal"
      ariaLabel={`Detalle de ticket ${item.ticket || item.title}`}
      header={
        <JiraModalHeader
          item={item}
          issueType={issueType}
          priority={priority}
          workflow={workflow}
          availableTransitions={availableTransitions}
          nextWorkflow={nextWorkflow}
          jiraBrowseUrl={jiraBrowseUrl}
          copiedKey={copiedKey}
          onCopyKey={handleCopyKey}
          onUpdateWorkflow={updateWorkflowState}
          onClose={onClose}
        />
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 24,
          padding: '24px 28px',
          flexWrap: 'wrap',
        }}
      >
        <main
          style={{
            flex: '1 1 560px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          <h1
            data-testid="jira-modal-title"
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.35,
              letterSpacing: '-0.02em',
              color: 'var(--z-text)',
            }}
          >
            {item.title}
          </h1>

          <JiraModalAiBanner
            aiText={item.ai || ''}
            summaryBusy={summaryBusy}
            onGenerateSummary={handleGenerateSummary}
          />

          <JiraModalDescription description={description} />

          {item.ticket ? (
            <ProviderCommentComposer
              platform="jira"
              data-testid="jira-comment-composer"
              onSubmit={(markdown) => addJiraComment(item.ticket!, markdown)}
              onGenerateDraft={(hint) => generateJiraCommentDraft(item.ticket!, hint)}
              onMentionCandidateUnavailable={() => openSettingsSection('people')}
            />
          ) : null}

          <div data-testid="jira-modal-criteria-section">
            <JiraCriteriaSection
              ticket={item.ticket}
              criteria={item.criteria}
              onToggle={toggleAcceptanceCriterion}
              onAdd={addAcceptanceCriterion}
              onUpdate={updateAcceptanceCriterion}
              onDelete={deleteAcceptanceCriterion}
            />
          </div>

          {item.subtasks && item.subtasks.length > 0 ? (
            <div data-testid="jira-modal-subtasks-section">
              <JiraSubtasksSection
                ticket={item.ticket}
                subtasks={item.subtasks}
                onCycleState={cycleSubtaskState}
              />
            </div>
          ) : null}

          <JiraModalLinkedPrs
            linkedPullRequests={linkedPullRequests}
            onNavigateToGitHub={handleNavigateToGitHub}
          />

          <JiraModalRefs
            refs={item.refs || []}
            onNavigateToJira={handleNavigateToJira}
            isJiraRefResolved={(key) => Boolean(getJiraItemByKey(key))}
          />
        </main>

        <JiraModalSidebar
          holdInfo={holdInfo}
          assignee={rawFields?.assignee}
          reporter={rawFields?.reporter}
          sprint={rawFields?.sprint}
          storyPoints={rawFields?.storyPoints}
          components={item.components || rawFields?.components || []}
          labels={item.labels || rawFields?.labels || []}
          createdAt={rawFields?.createdAt}
          updatedAt={rawFields?.updatedAt || item.updatedAt}
          externalSignals={crossRefs.externalSignals}
        />
      </div>
    </ProviderItemDetail>
  );
};
